import { useState, useRef, useEffect, useMemo } from "react";

import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { themeManagementSchema } from "../../../validation/themeManagementSchema";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Slide } from "react-toastify";

import {
  MdExpandMore,
  MdMoreVert,
  MdPublic,
  MdCheck,
  MdExpandLess,
  MdAddCircleOutline,
  MdRemoveCircleOutline,
  MdOutlineEdit,
} from "react-icons/md";
import { MdSave } from "react-icons/md";
import { AiOutlineInstagram, AiOutlineFacebook } from "react-icons/ai";
import { BiLogoTiktok } from "react-icons/bi";

const ThemeManagement = () => {
  const [sections, setSections] = useState({
    storeIdentity: true,
    themeDetails: true,
    socialMedia: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const toggleSection = (section) => {
    setSections({ ...sections, [section]: !sections[section] });
  };

  // file implementation and validation
  const [storeLogo, setStoreLogo] = useState(null);

  // Handle Image Selection
  const handleFileChange = (event) => {
    const file = event.target.files[0]; // Get the first selected file

    if (file) {
      // Validate file type (Only PNG, JPG, SVG)
      const validTypes = ["image/png", "image/jpeg", "image/svg+xml"];
      if (!validTypes.includes(file.type)) {
        setError("Invalid file format. Only PNG, JPG, or SVG allowed.");
        return;
      }

      // Validate file size (Max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError("File size must be under 2MB.");
        return;
      }

      // Clear error and set image for preview
      setError("");
      setStoreLogo(URL.createObjectURL(file)); // Create preview URL
    }
  };

  // telephone number function
  const [phoneNumbers, setPhoneNumbers] = useState([{ id: 1, number: "", countryCode: "+254" }]);

  // opening hours function
  const [schedules, setSchedules] = useState([
    { id: 1, dayStart: "Monday", dayEnd: "Friday", openTime: "", closeTime: "" },
  ]);

  const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  // save function (toastify)
  const handleSave = async () => {
    // Show loading message
    const toastId = toast.info("Saving changes...", {
      autoClose: false, // Keep it open until manually dismissed
      hideProgressBar: true, // No progress bar
      className: "custom-toast-info",
      transition: Slide,
      closeButton: false,
    });

    try {
      await new Promise((resolve) => setTimeout(resolve, 2000)); // Simulated delay

      // Remove loading toast before showing success
      toast.dismiss(toastId);

      // Show success message
      toast.success("Changes saved successfully!", {
        autoClose: 5000, // Stay for 5 sec then disappear
        hideProgressBar: true, // No progress bar
        className: "custom-toast-success",
        transition: Slide,
        closeButton: false,
      });
    } catch (error) {
      toast.dismiss(toastId); // Remove loading toast before showing error

      // Show error message
      toast.error("Failed to save changes!", {
        autoClose: 5000,
        hideProgressBar: true,
        className: "custom-toast-error",
        closeButton: false,
      });
    }
  };

  // theme selection function
  const [showDropdown, setShowDropdown] = useState(null);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const buttonRefs = useRef({});
  const [themes, setThemes] = useState([
    {
      id: 1,
      name: "UOMO",
      themeId: "#123456",
      isActive: true,
      imageUrl: "",
    },
    {
      id: 2,
      name: "Random Name",
      themeId: "#654321",
      isActive: false,
      imageUrl: "https://via.placeholder.com/100",
    },
  ]);
  const renderedThemes = useMemo(() => themes, [themes]);

  const [editingThemeId, setEditingThemeId] = useState(null);
  const [newThemeName, setNewThemeName] = useState("");

  const handleRename = (themeId, index) => {
    if (newThemeName.length < 2) return; // Prevent saving invalid names

    // Get existing saved names from localStorage
    const storedNames = JSON.parse(localStorage.getItem("customThemeNames")) || {};

    // Update the stored name for this theme
    storedNames[themeId] = newThemeName;

    // Save back to localStorage
    localStorage.setItem("customThemeNames", JSON.stringify(storedNames));

    setValue(`themes.${index}.name`, newThemeName, { shouldValidate: true });

    // Update the UI
    setEditingThemeId(null);
  };
  const handleActivate = (themeId) => {
    if (!themes.some((theme) => theme.id === themeId && theme.isActive)) {
      setThemes(
        themes.map((theme) =>
          theme.id === themeId ? { ...theme, isActive: true } : { ...theme, isActive: false }
        )
      );
    }
  };
  const handleDeactivate = (themeId) => {
    setThemes(
      themes.map((theme) => (theme.id === themeId ? { ...theme, isActive: false } : theme))
    );
    setShowDropdown(null);
  };
  const handleOpenDropdown = (themeId, event) => {
    const buttonRect = buttonRefs.current[themeId]?.getBoundingClientRect();
    setModalPosition({
      top: buttonRect.bottom + 5, // Positions it below the button
      left: buttonRect.left, // Aligns with button horizontally
    });
    setShowDropdown(themeId);
  };
  // close modal on scrolling
  useEffect(() => {
    if (showDropdown !== null) {
      const handleScroll = () => {
        setShowDropdown(null);
      };

      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [showDropdown]);

  // initializing react hook and zod form validation
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(themeManagementSchema),
    mode: "onChange", // Enables real-time validation
    defaultValues: {
      storeName: "",
      storeLogo: null,
      storeDescription: "",
      phoneNumbers: [{ countryCode: "+254", number: "" }],
      emails: [{ email: "" }],
      schedules: [{ dayStart: "", dayEnd: "", openTime: "", closeTime: "" }],
      themeName: "",
      socialMediaLinks: { facebook: "", instagram: "", tiktok: "" },
    },
  });

  // Manage dynamic phone numbers field
  const {
    fields: PhoneFields,
    append: addPhoneNumber,
    remove: removePhoneNumber,
  } = useFieldArray({
    control,
    name: "phoneNumbers",
  });
  // Manage dynamic email field
  const {
    fields: emailFields,
    append: addEmail,
    remove: removeEmail,
  } = useFieldArray({
    control,
    name: "emails",
  });
  // Use useFieldArray for dynamic schedule management
  const {
    fields: scheduleFields,
    append: addSchedule,
    remove: removeSchedule,
  } = useFieldArray({
    control,
    name: "schedules",
  });

  // Submission Handler
  const onSubmit = async (data) => {
    try {
      toast.info("Saving...");
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate API call
      toast.success("Changes saved successfully!");
      
    } catch (error) {
      toast.error("Failed to save changes.");
    }
  };

  return (
    <div className="max-w-[80em] mx-auto p-6">
      <h2 className="text-2xl font-semibold mb-4">Theme Management</h2>
      <p className="text-gray-600 text-sm mb-4">
        Manage your store's branding, themes, and social media presence.
      </p>

      {/* Store Identity Section */}
      <div className="bg-white rounded-lg mb-4">
        <div className="flex flex-row justify-between items-center border-b p-[1em] border-[#d7d7d7] w-full ">
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold">Store Identity</h3>
            <span className="text-[#7e7e7e]">
              Customize your store's branding with a name, logo, and related information to ensure a
              consistent identity across your website
            </span>
          </div>
          <span className="cursor-pointer" onClick={() => toggleSection("storeIdentity")}>
            {sections.storeIdentity ? <MdExpandLess /> : <MdExpandMore />}
          </span>
        </div>
        {sections.storeIdentity && (
          <div className="mt-4 py-5 px-[3em]">
            {/* store name field */}
            <label className="block text-sm font-medium">Store Name</label>
            <input
              type="text"
              {...register("storeName")}
              placeholder="Enter your store name"
              className={`w-full p-2 border rounded-sm ${errors.storeName ? "border-red-500" : "border-gray-300"}`}
            />
            {/* Show error message */}
            {errors.storeName && (
              <p className="text-red-500 text-sm mt-1">{errors.storeName.message}</p>
            )}

            {/* store logo field */}
            <label className="block text-sm font-medium mt-4">Store Logo</label>
            {/* fix image aspect ratio */}
            <div className="flex flex-row items-center gap-[1em]">
              <div className="flex items-center justify-center border border-[#d7d7d7] w-[9em] h-[9em]">
                {storeLogo ? (
                  <img src={storeLogo} alt="Store Logo" className="object-cover w-full h-full" />
                ) : (
                  <span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="45"
                      height="45"
                      viewBox="0 0 30 30"
                      fill="none"
                    >
                      <path
                        d="M16.875 26.2498V16.8748C16.875 16.3571 17.2947 15.9373 17.8125 15.9373H21.5625C22.0803 15.9373 22.5 16.3571 22.5 16.8748V26.2498M16.875 26.2498H2.9511M16.875 26.2498H22.5M22.5 26.2498H27.0489M25.3125 26.2498V11.6858M4.6875 26.2498V11.6861M4.6875 11.6861C6.12162 12.515 7.98722 12.316 9.21415 11.0891C9.26993 11.0333 9.32358 10.9762 9.3751 10.9179C10.0622 11.6964 11.0675 12.1873 12.1875 12.1873C13.3075 12.1873 14.3129 11.6963 15 10.9178C15.6871 11.6963 16.6925 12.1873 17.8125 12.1873C18.9324 12.1873 19.9376 11.6964 20.6248 10.918C20.6762 10.9763 20.7298 11.0333 20.7855 11.089C22.0125 12.316 23.8783 12.5149 25.3125 11.6858M4.6875 11.6861C4.40986 11.5256 4.14838 11.3266 3.91085 11.0891C2.44638 9.62462 2.44638 7.25025 3.91085 5.78578L5.39746 4.29917C5.74909 3.94754 6.226 3.75 6.72328 3.75H23.2765C23.7738 3.75 24.2507 3.94754 24.6023 4.29918L26.0888 5.78568C27.5533 7.25014 27.5533 9.62451 26.0888 11.089C25.8514 11.3264 25.59 11.5253 25.3125 11.6858M8.4375 22.4998H13.125C13.6428 22.4998 14.0625 22.0801 14.0625 21.5623V16.8748C14.0625 16.3571 13.6428 15.9373 13.125 15.9373H8.4375C7.91973 15.9373 7.5 16.3571 7.5 16.8748V21.5623C7.5 22.0801 7.91973 22.4998 8.4375 22.4998Z"
                        stroke="#D4D4D4"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </span>
                )}
              </div>

              <div className="border border-[#d7d7d7] h-[9em] w-[60%]">
                <label className="flex flex-col items-center justify-center h-full p-6 cursor-pointer hover:bg-[#f5f5f5]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M2.25 15.75L7.40901 10.591C8.28769 9.71231 9.71231 9.71231 10.591 10.591L15.75 15.75M14.25 14.25L15.659 12.841C16.5377 11.9623 17.9623 11.9623 18.841 12.841L21.75 15.75M3.75 19.5H20.25C21.0784 19.5 21.75 18.8284 21.75 18V6C21.75 5.17157 21.0784 4.5 20.25 4.5H3.75C2.92157 4.5 2.25 5.17157 2.25 6V18C2.25 18.8284 2.92157 19.5 3.75 19.5ZM14.25 8.25H14.2575V8.2575H14.25V8.25ZM14.625 8.25C14.625 8.45711 14.4571 8.625 14.25 8.625C14.0429 8.625 13.875 8.45711 13.875 8.25C13.875 8.04289 14.0429 7.875 14.25 7.875C14.4571 7.875 14.625 8.04289 14.625 8.25Z"
                      stroke="#848484"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <p className="font-medium text-black">Click to upload your image</p>
                  <p className="text-sm text-gray-500">PNG or JPEG (Max 2 MB)</p>
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                    accept="image/png, image/jpeg"
                  />
                </label>
              </div>
            </div>
            {/* Show error message if file is invalid */}
            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

            {/* store description field */}
            <label className="block text-sm font-medium mt-4">Store Description</label>
            <textarea
              {...register("storeDescription")}
              placeholder="Enter a short store description"
              className="w-full p-2 border rounded-md"
            />
            {/* Show error message */}
            {errors.storeDescription && (
              <p className="text-red-500 text-sm mt-1">{errors.storeDescription.message}</p>
            )}

            <div className="border-b border-[#d7d7d7] my-[1.5em]"></div>

            {/* telephone number field */}
            <div className="space-y-3">
              <label className="block text-sm font-medium">Telephone Number</label>
              {PhoneFields.map((phone, index) => (
                <div key={phone.id} className="flex gap-2">
                  <select
                    {...register(`phoneNumbers.${index}.countryCode`)}
                    className="p-2 border rounded-md"
                  >
                    <option value="+254">🇰🇪 +254 (Kenya)</option>
                    <option value="+255">🇹🇿 +255 (Tanzania)</option>
                    <option value="+256">🇺🇬 +256 (Uganda)</option>
                    <option value="+250">🇷🇼 +250 (Rwanda)</option>
                  </select>
                  <div className="flex flex-col w-[65%]">
                    <input
                      {...register(`phoneNumbers.${index}.number`)}
                      type="text"
                      placeholder="Enter phone number"
                      className={`p-2 border rounded-md  ${
                        errors.phoneNumbers?.[index]?.number ? "border-red-500" : ""
                      }`}
                    />
                    {/* Display error messages */}
                    {errors.phoneNumbers?.[index]?.number && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.phoneNumbers[index].number.message}
                      </p>
                    )}
                  </div>

                  {index === 0 ? (
                    <button
                      type="button"
                      onClick={() => addPhoneNumber({ countryCode: "+254", number: "" })}
                      className="text-[#000000] h-fit"
                    >
                      <MdAddCircleOutline size={24} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => removePhoneNumber(index)}
                      className="text-red-600 h-fit"
                    >
                      <MdRemoveCircleOutline size={24} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="border-b border-[#d7d7d7] my-[1.5em]"></div>

            {/* store email field */}
            <div className="space-y-3">
              <label className="block text-sm font-medium">Email Address</label>
              {emailFields.map((email, index) => (
                <div key={email.id} className="flex  gap-2">
                  <div className="flex flex-col w-[80.3%]">
                    <input
                      {...register(`emails.${index}.email`)}
                      type="email"
                      placeholder="Enter email address"
                      className={`p-2 border rounded-md ${
                        errors.emails?.[index]?.email ? "border-red-500" : ""
                      }`}
                    />

                    {/* Show error message */}
                    {errors.emails?.[index]?.email && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.emails[index].email.message}
                      </p>
                    )}
                  </div>

                  {index === 0 ? (
                    <button
                      type="button"
                      onClick={() => addEmail("")}
                      className="text-[#000000] h-fit"
                    >
                      <MdAddCircleOutline size={24} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => removeEmail(index)}
                      className="text-red-600 h-fit"
                    >
                      <MdRemoveCircleOutline size={24} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="border-b border-[#d7d7d7] my-[1.5em]"></div>

            {/* opening hours field */}
            <div className="space-y-4">
              <label className="block text-sm font-medium">Store Opening Hours</label>
              {scheduleFields.map((schedule, index) => (
                <div
                  key={schedule.id}
                  className="flex flex-wrap items-center gap-2 border p-3 rounded-md"
                >
                  {/* Day Start Dropdown */}
                  <div>
                    <select
                      {...register(`schedules.${index}.dayStart`)}
                      className="p-2 border rounded-md"
                    >
                      <option value="">Start Day</option>
                      {daysOfWeek.map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>
                    {/* Show Day Start Error */}
                    {errors.schedules?.[index]?.dayStart && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.schedules[index].dayStart.message}
                      </p>
                    )}
                  </div>

                  <span>to</span>

                  {/* Day End Dropdown */}
                  <div>
                    <select
                      {...register(`schedules.${index}.dayEnd`)}
                      className="p-2 border rounded-md"
                    >
                      <option value="">End Day</option>
                      {daysOfWeek.map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>
                    {/* Show Day End Error */}
                    {errors.schedules?.[index]?.dayEnd && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.schedules[index].dayEnd.message}
                      </p>
                    )}
                  </div>

                  {/* Open Time Picker */}
                  <div>
                    <div className="flex items-center border p-2 rounded-md w-fit">
                      <input
                        {...register(`schedules.${index}.openTime`)}
                        type="time"
                        className="outline-none"
                      />
                    </div>
                    {/* Show Open Time Error */}
                    {errors.schedules?.[index]?.openTime && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.schedules[index].openTime.message}
                      </p>
                    )}
                  </div>

                  <span>to</span>

                  {/* Close Time Picker */}
                  <div>
                    <div className="flex items-center border p-2 rounded-md w-fit">
                      <input
                        {...register(`schedules.${index}.closeTime`)}
                        type="time"
                        className="outline-none"
                      />
                    </div>
                    {/* Show Close Time Error */}
                    {errors.schedules?.[index]?.closeTime && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.schedules[index].closeTime.message}
                      </p>
                    )}
                  </div>

                  {/* Add/Remove Button */}
                  {index === 0 ? (
                    <button
                      type="button"
                      onClick={() =>
                        addSchedule({ dayStart: "", dayEnd: "", openTime: "", closeTime: "" })
                      }
                      className="text-blue-600"
                    >
                      <MdAddCircleOutline size={24} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => removeSchedule(index)}
                      className="text-red-600"
                    >
                      <MdRemoveCircleOutline size={24} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {/* Show error message */}
            {errors.schedules && (
              <p className="text-red-500 text-sm mt-1">{errors.schedules.message}</p>
            )}
          </div>
        )}
      </div>

      {/* Theme Details Section */}
      <div className="bg-white rounded-lg mb-4">
        <div className="flex flex-row justify-between items-center border-b p-[1em] border-[#d7d7d7] w-full ">
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold">Theme details</h3>
            <span className="text-[#7e7e7e]">
              Select and configure your store’s theme to match your brand identity and provide a
              seamless shopping experience.
            </span>
          </div>
          <span className="cursor-pointer" onClick={() => toggleSection("themeDetails")}>
            {sections.storeIdentity ? <MdExpandLess /> : <MdExpandMore />}
          </span>
        </div>
        {sections.themeDetails && (
          <div className="bg-white p-4 mb-4">
            <label className="block text-sm font-medium">Bought themes:</label>

            {renderedThemes.map((theme, index) => {
              // Retrieve stored custom theme names
              const storedNames = JSON.parse(localStorage.getItem("customThemeNames")) || {};
              const themeDisplayName = storedNames[theme.themeId] || theme.name;

              return (
                <div
                  key={theme.id}
                  className="flex items-center justify-between border p-4 rounded-md bg-white mt-3"
                >
                  {/* Theme Preview */}
                  <div className="flex items-center">
                    {theme.imageUrl && !theme.imageError ? (
                      <img
                        src={theme.imageUrl}
                        alt={theme.name}
                        className="w-16 h-16 rounded-md mr-4"
                      />
                    ) : (
                      <MdPublic className="w-16 h-16 text-gray-400 mr-4" />
                    )}

                    <div>
                      {editingThemeId === theme.id ? (
                        <>
                          <input
                            {...register(`themes.${index}.name`)}
                            type="text"
                            className="border p-1 rounded-md"
                            value={newThemeName} // Ensures proper input control
                            onChange={(e) => setNewThemeName(e.target.value)}
                            onBlur={() => handleRename(theme.themeId, index)}
                            onKeyDown={(e) =>
                              e.key === "Enter" && handleRename(theme.themeId, index)
                            }
                            autoFocus
                          />
                          {errors.themes?.[index]?.name && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.themes[index].name.message}
                            </p>
                          )}
                        </>
                      ) : (
                        <div>
                          <p
                            className="font-medium cursor-pointer flex items-center gap-[1em]"
                            onClick={() => {
                              setEditingThemeId(theme.id);
                              setNewThemeName(themeDisplayName); // Load stored name instead of empty string
                            }}
                          >
                            {themeDisplayName}
                            <MdOutlineEdit />
                          </p>
                        </div>
                      )}
                      <p className="text-sm text-gray-500">Theme ID: {theme.themeId}</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="relative flex gap-[1em]">
                    {/* Three-dot button */}
                    <button
                      ref={(el) => (buttonRefs.current[theme.id] = el)}
                      onClick={(event) => handleOpenDropdown(theme.id, event)}
                      className="p-2 border border-[#bebebe] rounded-lg shadow-sm hover:bg-gray-200"
                    >
                      <MdMoreVert size={20} />
                    </button>

                    {/* Dropdown Modal */}
                    {showDropdown === theme.id && (
                      <div className="fixed inset-0 z-10" onClick={() => setShowDropdown(null)}>
                        <div
                          className="absolute mt-2 w-40 rounded-lg shadow-md bg-white border border-[#d7d7d7]"
                          style={{
                            top: modalPosition.top,
                            left: modalPosition.left,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            className="block w-full text-left px-4 py-2 border-b border-[#d7d7d7] hover:bg-gray-100"
                            onClick={() => alert("Previewing Theme")}
                          >
                            Preview
                          </button>
                          {theme.isActive && (
                            <button
                              className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-red-600"
                              onClick={() => handleDeactivate(theme.id)}
                            >
                              Deactivate
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Status Button */}
                    {theme.isActive ? (
                      <span className="bg-green-100 text-green-700 px-3 py-1 rounded-md flex items-center">
                        <MdCheck className="mr-1" /> Current Theme
                      </span>
                    ) : (
                      <button
                        className="border border-[#bebebe] px-3 py-1 rounded-md shadow-sm hover:bg-gray-100"
                        onClick={() => handleActivate(theme.id)}
                      >
                        Activate
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Social Media Section */}
      <div className="bg-white rounded-lg mb-4">
        <div className="flex flex-row justify-between items-center border-b p-[1em] border-[#d7d7d7] w-full ">
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold">Social media information</h3>
            <span className="text-[#7e7e7e]">
              Manage your store’s contact details and social media links to enhance customer
              engagement and accessibility.
            </span>
          </div>
          <span className="cursor-pointer" onClick={() => toggleSection("socialMedia")}>
            {sections.storeIdentity ? <MdExpandLess /> : <MdExpandMore />}
          </span>
        </div>
        {sections.socialMedia && (
          <div className="mt-4 p-4">
            {/* facebook */}
            <div className="flex flex-row gap-[0.5em] items-center w-fit mt-4">
              <AiOutlineFacebook />
              <label className="block text-md font-medium">Facebook</label>
            </div>
            <input
              {...register("socialMedia.facebook")}
              type="text"
              placeholder="https://facebook.com/yourpage"
              className="w-full p-2 border rounded-md"
              onChange={(e) =>
                setValue("socialMedia.facebook", e.target.value, { shouldValidate: true })
              }
            />
            {errors.socialMedia?.facebook && (
              <p className="text-red-500 text-sm mt-1">{errors.socialMedia.facebook.message}</p>
            )}
            {/* Instagram */}
            <div className="flex flex-row gap-[0.5em] items-center w-fit mt-4">
              <AiOutlineInstagram />
              <label className="block text-md font-medium">Instagram</label>
            </div>
            <input
              {...register("socialMedia.instagram")}
              type="text"
              placeholder="https://instagram.com/yourprofile"
              className="w-full p-2 border rounded-md"
              onChange={(e) =>
                setValue("socialMedia.instagram", e.target.value, { shouldValidate: true })
              }
            />
            {errors.socialMedia?.instagram && (
              <p className="text-red-500 text-sm mt-1">{errors.socialMedia.instagram.message}</p>
            )}
            {/* tiktok */}
            <div className="flex flex-row gap-[0.5em] items-center w-fit mt-4">
              <BiLogoTiktok />
              <label className="block text-md font-medium">TikTok</label>
            </div>
            <input
              {...register("socialMedia.tiktok")}
              type="text"
              placeholder="https://tiktok.com/@yourusername"
              className="w-full p-2 border rounded-md"
              onChange={(e) =>
                setValue("socialMedia.tiktok", e.target.value, { shouldValidate: true })
              }
            />
            {errors.socialMedia?.tiktok && (
              <p className="text-red-500 text-sm mt-1">{errors.socialMedia.tiktok.message}</p>
            )}
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-center items-center w-full">
        <button
          onClick={handleSave}
          className="bg-[#000000] text-white px-4 py-2 rounded-md hover:bg-[#333333] transition w-[50%] flex items-center justify-center"
        >
          {loading ? "Saving..." : "Save Changes"}
          {loading && <MdSave className="ml-2 animate-spin" />}
        </button>
      </div>

      {/* Toast Container for Notifications */}
      <ToastContainer position="bottom-center" autoClose={2000} />
    </div>
  );
};

export default ThemeManagement;
